import { useEffect, useMemo, useRef, useState } from 'react';
import { activeWordAt, formatTimestamp, type TranscriptWord } from '@captionflow/caption-engine';
import { useEditor } from '../../state/editorStore';
import { api } from '../../api/client';
import { playbackClock } from '../../playback/clock';
import { TRANSLATE_LANGUAGES, SOURCE_LANGUAGE_OPTIONS } from '@captionflow/shared';

/** Word-level transcript: segments with timestamps, each word a chip you can
 *  click to select (and seek). The selected word drives the right inspector. */
export function StudioTranscript() {
  const words = useEditor((s) => s.words);
  const segments = useEditor((s) => s.segments);
  const selectedWordId = useEditor((s) => s.selectedWordId);
  const selectWord = useEditor((s) => s.selectWord);
  const mergeSegmentWithNext = useEditor((s) => s.mergeSegmentWithNext);
  const splitSegmentAt = useEditor((s) => s.splitSegmentAt);
  const projectId = useEditor((s) => s.projectId);
  const language = useEditor((s) => s.language);
  const setWordsText = useEditor((s) => s.setWordsText);
  const [tab, setTab] = useState<'transcript' | 'script'>('transcript');
  const [busy, setBusy] = useState<'idle' | 'regen' | 'translate'>('idle');
  const [note, setNote] = useState<string | null>(null);
  const [langOpen, setLangOpen] = useState(false);
  // Whether the Regenerate source-language menu is open. Regenerate always asks
  // for the spoken language (no silent default), so a language is chosen each run.
  const [regenOpen, setRegenOpen] = useState(false);
  const [activeWordId, setActiveWordId] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const activeRef = useRef<HTMLButtonElement>(null);

  const byId = useMemo(() => new Map(words.map((w) => [w.id, w])), [words]);

  useEffect(() => {
    let last = 0;
    return playbackClock.subscribe((t) => {
      const now = performance.now();
      if (now - last < 100) return;
      last = now;
      setActiveWordId(activeWordAt(words, t)?.id ?? null);
    });
  }, [words]);

  // Auto-scroll: keep the active word centred in the transcript list while the
  // video plays (don't fight the user's manual scroll when paused).
  useEffect(() => {
    if (tab !== 'transcript' || !playbackClock.isPlaying()) return;
    const container = scrollRef.current;
    const el = activeRef.current;
    if (!container || !el) return;
    const cRect = container.getBoundingClientRect();
    const eRect = el.getBoundingClientRect();
    const delta = eRect.top - cRect.top - (container.clientHeight / 2 - eRect.height / 2);
    if (Math.abs(delta) > 4) container.scrollBy({ top: delta, behavior: 'smooth' });
  }, [activeWordId, tab]);

  const pick = (w: TranscriptWord) => {
    selectWord(w.id);
    // A transcript click is a single selection - drop any timeline multi-select.
    useEditor.getState().setSelectedWords([w.id]);
    playbackClock.seek(w.startMs);
  };

  // Regenerate: re-transcribe from the source audio, then reload the fresh
  // transcript. Replaces words, breaks and emphasis.
  const regenerate = async (lang: { label: string; code: string }) => {
    setRegenOpen(false);
    if (!projectId || busy !== 'idle') return;
    if (!window.confirm(`Regenerate the transcript in ${lang.label} from the source audio? This replaces the current words, breaks and emphasis.`))
      return;
    setBusy('regen');
    setNote(`Regenerating transcript in ${lang.label}… this can take a minute.`);
    const before = `${words.length}|${words[0]?.text ?? ''}`;
    try {
      await api.retranscribe(projectId, lang.code);
      const started = Date.now();
      for (;;) {
        await new Promise((r) => setTimeout(r, 3000));
        const t = await api.getTranscript(projectId);
        const sig = `${t.words.length}|${t.words[0]?.text ?? ''}`;
        const done = t.status === 'completed' && sig !== before && t.words.length > 0;
        if (done || Date.now() - started > 240_000) {
          if (t.words.length > 0) {
            useEditor.getState().load({
              words: t.words.map((w) => ({
                id: w.id,
                text: w.text,
                startMs: w.startMs,
                endMs: w.endMs,
                confidence: w.confidence,
                emphasized: w.emphasized,
                hidden: w.hidden,
                isFiller: w.isFiller,
              })),
              templateId: useEditor.getState().templateId,
              durationMs: t.durationMs ?? 0,
              videoUrl: t.sourceUrl,
            });
            useEditor.setState({ language: t.language });
          }
          setNote(done ? 'Regenerated. Review the new transcript.' : 'Reloaded the latest transcript.');
          break;
        }
      }
    } catch {
      setNote('Regenerate failed - the video may still be processing. Try again shortly.');
    } finally {
      setBusy('idle');
    }
  };

  // Translate: word-aligned so timings are preserved. Applies in place and saves.
  const translateTo = async (target: string) => {
    setLangOpen(false);
    if (!projectId || busy !== 'idle') return;
    setBusy('translate');
    setNote(`Translating to ${target}…`);
    try {
      const payload = words.map((w) => ({ id: w.id, text: w.text, startMs: w.startMs, endMs: w.endMs }));
      const res = await api.translate(projectId, payload, target, language);
      if (res.source === 'unavailable') {
        setNote('Translation is unavailable right now (AI key missing or the model failed).');
      } else {
        setWordsText(res.words);
        useEditor.setState({ language: res.language });
        setNote(`Translated to ${target}.`);
      }
    } catch {
      setNote('Translate failed - try again in a moment.');
    } finally {
      setBusy('idle');
    }
  };

  // Split the segment at the playhead: after the last word that has started.
  const splitAtPlayhead = (segId: string, memberWords: TranscriptWord[]) => {
    const t = playbackClock.timeMs();
    let idx = memberWords.findIndex((w) => w.startMs > t);
    if (idx === -1) idx = memberWords.length - 1;
    else idx = Math.max(0, idx - 1);
    if (idx >= 0 && idx < memberWords.length - 1) splitSegmentAt(segId, idx);
  };

  const disabled = busy !== 'idle' || !projectId;

  return (
    <div className="sl-body">
      <div className="sl-actions">
        {/* Regenerate always asks which language to transcribe in — pick one and
            it re-runs STT in that language (and remembers it on the project). */}
        <div className="sl-act-wrap">
          <button
            className="sl-act"
            onClick={() => setRegenOpen((v) => !v)}
            disabled={disabled}
            title={projectId ? 'Re-transcribe from the source audio in a chosen language' : 'Open a project to regenerate'}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
              <path d="M21 12a9 9 0 1 1-2.64-6.36M21 3v5h-5" />
            </svg>
            {busy === 'regen' ? 'Regenerating…' : 'Regenerate'}
            <span className="sl-caret">▾</span>
          </button>
          {regenOpen && (
            <>
              <button className="sl-langbackdrop" aria-hidden onClick={() => setRegenOpen(false)} tabIndex={-1} />
              <div className="sl-langmenu" role="menu">
                <div className="sl-langmenu-head">Spoken language</div>
                {SOURCE_LANGUAGE_OPTIONS.map((l) => (
                  <button key={`${l.label}-${l.code}`} role="menuitem" onClick={() => regenerate(l)}>
                    {l.label}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
        <div className="sl-act-wrap">
          <button className="sl-act" onClick={() => setLangOpen((v) => !v)} disabled={disabled}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
              <path d="M4 5h10M9 3v2c0 5-2.5 8-6 9M6 9c0 3 3.5 5 6 5M13 20l4-9 4 9M14.5 17h5" />
            </svg>
            {busy === 'translate' ? 'Translating…' : 'Translate'}
            <span className="sl-caret">▾</span>
          </button>
          {langOpen && (
            <>
              <button className="sl-langbackdrop" aria-hidden onClick={() => setLangOpen(false)} tabIndex={-1} />
              <div className="sl-langmenu" role="menu">
              {TRANSLATE_LANGUAGES.map((l) => (
                <button key={l} role="menuitem" onClick={() => translateTo(l)}>
                  {l}
                  {l === 'Hinglish' && <span className="sl-langtag">Roman Hindi</span>}
                </button>
              ))}
              </div>
            </>
          )}
        </div>
      </div>
      {note && <div className="sl-note">{note}</div>}
      <div className="sl-tabs">
        <button className={tab === 'transcript' ? 'on' : ''} onClick={() => setTab('transcript')}>
          Transcript
        </button>
        <button className={tab === 'script' ? 'on' : ''} onClick={() => setTab('script')}>
          Script
        </button>
      </div>

      {tab === 'script' ? (
        <div className="sl-script">
          {segments.map((seg) => (
            <p key={seg.id}>
              {seg.wordIds.map((id) => byId.get(id)?.text).filter(Boolean).join(' ')}
            </p>
          ))}
        </div>
      ) : (
        <div className="sl-segments" ref={scrollRef}>
          {segments.map((seg, i) => {
            const memberWords = seg.wordIds
              .map((id) => byId.get(id))
              .filter((w): w is TranscriptWord => Boolean(w));
            return (
              <div key={seg.id} className="sl-seg">
                <div className="sl-seg-time">
                  {formatTimestamp(seg.startMs)}–{formatTimestamp(seg.endMs)}
                </div>
                <div className="sl-seg-words">
                  {memberWords.map((w) => (
                    <button
                      key={w.id}
                      ref={w.id === activeWordId ? activeRef : undefined}
                      className={[
                        'sl-word',
                        w.id === selectedWordId && 'sel',
                        w.id === activeWordId && 'active',
                        w.emphasized && 'emph',
                      ]
                        .filter(Boolean)
                        .join(' ')}
                      onClick={() => pick(w)}
                    >
                      {w.text}
                    </button>
                  ))}
                </div>
                <div className="sl-seg-actions">
                  <button onClick={() => splitAtPlayhead(seg.id, memberWords)}>✂ Split at playhead</button>
                  {i < segments.length - 1 && (
                    <button onClick={() => mergeSegmentWithNext(seg.id)}>⊟ Merge next</button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
